// MyDesk — class detail page. Shows chapters (subjects) each expanded with
// dated lesson cards. Clicking a lesson opens the Learn Now split-screen.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Calendar, Play, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../studentApi';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

export default function MyDeskClassPage() {
  const { classId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/student/mydesk/class/${classId}`)
      .then(d => mounted && setData(d))
      .catch(e => mounted && setErr(e.message || 'Failed to load class'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [classId]);

  if (loading) return <div className="py-20 text-center text-sm" style={{ color: 'var(--st-mute)' }}>Loading class…</div>;
  if (err) return <div className="py-20 text-center text-sm text-red-500">{err}</div>;
  if (!data) return null;
  const { class: cls, chapters, total_lessons } = data;

  return (
    <div className="space-y-6" data-testid="mydesk-class-page">
      <Link to="/student/mydesk" className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--st-mute)' }} data-testid="mydesk-class-back">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to MyDesk
      </Link>

      <header className="rounded-2xl border p-5" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">
          {cls.grade || 'Class'}{cls.subject ? ` · ${cls.subject}` : ''}
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold mt-1" style={{ fontFamily: 'var(--st-font-heading)' }}>{cls.name}</h1>
        <div className="mt-2 text-xs" style={{ color: 'var(--st-mute)' }}>
          {cls.teacher_name && <span>Taught by {cls.teacher_name} · </span>}
          {chapters.length} chapter{chapters.length === 1 ? '' : 's'} · {total_lessons} lesson{total_lessons === 1 ? '' : 's'}
        </div>
      </header>

      {chapters.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed p-8 text-center text-sm" style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }} data-testid="mydesk-class-empty">
          Your teacher hasn't published anything yet. Check back soon.
        </div>
      ) : (
        <div className="space-y-3">
          {chapters.map((ch, i) => <ChapterBlock key={ch.chapter_id} chapter={ch} openByDefault={i === 0} classId={classId} />)}
        </div>
      )}
    </div>
  );
}

function ChapterBlock({ chapter, openByDefault, classId }) {
  const [open, setOpen] = useState(openByDefault);
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid={`mydesk-chapter-${chapter.chapter_id}`}>
      <button onClick={() => setOpen(v => !v)}
        data-testid={`mydesk-chapter-toggle-${chapter.chapter_id}`}
        className="w-full px-5 py-4 flex items-center justify-between text-left transition-all hover:bg-black/[0.03]">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Chapter</div>
          <h2 className="text-base sm:text-lg font-bold mt-0.5" style={{ fontFamily: 'var(--st-font-heading)' }}>{chapter.chapter_name}</h2>
          <div className="text-[11px] mt-1" style={{ color: 'var(--st-mute)' }}>{chapter.total} lesson{chapter.total === 1 ? '' : 's'}</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
      </button>

      {open && (
        <div className="border-t" style={{ borderColor: 'var(--st-border)' }}>
          {chapter.dates.map(d => (
            <div key={d.date} className="px-5 py-4 border-b last:border-b-0" style={{ borderColor: 'var(--st-border)' }}>
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--st-mute)' }}>
                <Calendar className="w-3 h-3" style={{ color: 'var(--st-primary)' }} />
                {fmtDate(d.date)}
              </div>
              <div className="space-y-2">
                {d.lessons.map(l => <LessonRow key={l.id} l={l} classId={classId} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LessonRow({ l, classId }) {
  return (
    <Link to={`/student/mydesk/lesson/${l.id}`}
      data-testid={`mydesk-lesson-${l.id}`}
      className="flex items-start gap-3 p-3 rounded-xl border transition-all hover:-translate-y-0.5"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: 'var(--st-card)', border: '1px solid var(--st-border)' }}>
        <Play className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-tight truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{l.title}</div>
        {l.summary && <div className="text-[11px] mt-1 opacity-70 line-clamp-2">{l.summary}</div>}
        <div className="inline-flex items-center gap-1 mt-1.5 text-[10px] uppercase tracking-wider font-bold opacity-60">
          {l.kind === 'audio' ? 'Audio lesson' : l.kind === 'lesson' ? 'Framework' : l.kind}
        </div>
      </div>
      <button
        className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white shrink-0"
        style={{ background: 'var(--st-primary)' }}
        data-testid={`mydesk-learn-${l.id}`}
        onClick={(e) => { e.preventDefault(); window.location.href = `/student/mydesk/lesson/${l.id}`; }}>
        Learn now
      </button>
    </Link>
  );
}
